import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Calendar, Download, Filter, MessageSquare, DollarSign, Scale, Users, FileText } from 'lucide-react';
import api from '@/services/api';
import { toast } from 'sonner';

interface TimelineEvent {
  id: number;
  date: string;
  title: string;
  description: string;
  event_type: string;
  severity: string;
  source_document_name: string;
  page_number?: number;
  amount?: number;
  participants: string[];
}

interface TimelineStats {
  total: number;
  counts: Record<string, number>;
  dateRange: {
    start: string | null;
    end: string | null;
  };
}

const eventTypeIcons: Record<string, any> = {
  communication: MessageSquare,
  financial: DollarSign,
  legal: Scale,
  custody: Users,
  other: Calendar,
};

const eventTypeColors: Record<string, string> = {
  communication: 'bg-blue-100 text-blue-800',
  financial: 'bg-green-100 text-green-800',
  legal: 'bg-purple-100 text-purple-800',
  custody: 'bg-orange-100 text-orange-800',
  other: 'bg-gray-100 text-gray-800',
};

const severityColors: Record<string, string> = {
  critical: 'bg-red-100 text-red-800 border-red-500',
  high: 'bg-orange-100 text-orange-800 border-orange-500',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-500',
  low: 'bg-green-100 text-green-800 border-green-500',
};

export default function Timeline() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [stats, setStats] = useState<TimelineStats | null>(null);

  useEffect(() => {
    loadTimeline();
  }, [projectId]);

  useEffect(() => {
    applyFilters();
  }, [events, filterType, startDate, endDate]);

  const loadTimeline = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/v1/timeline/projects/${projectId}`);
      setEvents(response.data.events);
      setStats({
        total: response.data.total_events,
        counts: response.data.event_counts,
        dateRange: response.data.date_range,
      });
    } catch (error) {
      toast.error('Failed to load timeline');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...events];

    if (filterType !== 'all') {
      filtered = filtered.filter(e => e.event_type === filterType);
    }

    if (startDate) {
      filtered = filtered.filter(e => e.date >= startDate);
    }
    if (endDate) {
      filtered = filtered.filter(e => e.date <= endDate);
    }

    setFilteredEvents(filtered);
  };

  const handleExport = async (format: 'pdf' | 'png') => {
    try {
      const response = await api.get(`/api/v1/timeline/projects/${projectId}/export`, {
        params: { format },
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], { 
        type: format === 'pdf' ? 'application/pdf' : 'image/png' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `timeline_${projectId}.${format}`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Timeline exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Export failed');
      console.error(error);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };

  const formatTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit' 
      });
    } catch {
      return '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">Loading timeline...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Case Timeline</h1>
        <p className="text-muted-foreground">
          Chronological view of all events and communications
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{stats?.total || 0}</div>
            <div className="text-sm text-muted-foreground">Total Events</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{stats?.counts?.communication || 0}</div>
            <div className="text-sm text-muted-foreground">Communications</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{stats?.counts?.financial || 0}</div>
            <div className="text-sm text-muted-foreground">Financial Events</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{stats?.counts?.legal || 0}</div>
            <div className="text-sm text-muted-foreground">Legal Events</div>
          </CardContent>
        </Card>
      </div>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters & Export
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
            >
              <option value="all">All Types</option>
              <option value="communication">Communication</option>
              <option value="financial">Financial</option>
              <option value="legal">Legal</option>
              <option value="custody">Custody</option>
              <option value="other">Other</option>
            </select>

            <Input
              type="date"
              placeholder="Start Date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />

            <Input
              type="date"
              placeholder="End Date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => handleExport('pdf')}>
                <Download className="h-4 w-4 mr-2" />
                PDF
              </Button>
              <Button variant="outline" onClick={() => handleExport('png')}>
                <Download className="h-4 w-4 mr-2" />
                PNG
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border" />
            
            <div className="space-y-8">
              {filteredEvents.map((event, index) => {
                const Icon = eventTypeIcons[event.event_type] || Calendar;
                const severityClass = severityColors[event.severity] || 'bg-gray-100 text-gray-800';
                const typeClass = eventTypeColors[event.event_type] || 'bg-gray-100 text-gray-800';

                return (
                  <div key={event.id} className="relative flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className={`z-10 flex h-16 w-16 items-center justify-center rounded-full border-4 border-background ${severityClass}`}>
                        <Icon className="h-6 w-6" />
                      </div>
                    </div>

                    <div className="flex-1 pb-8">
                      <div className="mb-2">
                        <div className="text-sm font-medium text-muted-foreground">
                          {formatDate(event.date)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatTime(event.date)}
                        </div>
                      </div>

                      <Card>
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <CardTitle className="text-base mb-2">{event.title}</CardTitle>
                              <div className="flex gap-2">
                                <Badge className={typeClass}>
                                  {event.event_type.replace('_', ' ')}
                                </Badge>
                                <Badge className={severityClass}>
                                  {event.severity}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm mb-2">{event.description}</p>
                          
                          {event.amount && (
                            <div className="text-sm font-medium text-green-600 mb-2">
                              Amount: ${event.amount.toLocaleString()}
                            </div>
                          )}

                          {event.participants && event.participants.length > 0 && (
                            <div className="text-xs text-muted-foreground mb-2">
                              Participants: {event.participants.join(', ')}
                            </div>
                          )}

                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <FileText className="h-3 w-3" />
                            <span>
                              {event.source_document_name}
                              {event.page_number && ` (Page ${event.page_number})`}
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                );
              })}
            </div>

            {filteredEvents.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                No events found matching your filters
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
